<template>
  <div class="relative" ref="container" @click.stop>
    <!-- Trigger -->
    <button
      ref="trigger"
      type="button"
      class="w-full flex items-center border border-gray-300 rounded px-3 py-2 text-sm text-left"
      :class="[
        open ? 'ring-1 ring-primary-300 border-primary-300' : 'hover:border-gray-400',
        disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'bg-white cursor-pointer',
      ]"
      :disabled="disabled"
      @click="toggle"
      @keydown.down.prevent="open ? focusNext() : toggle()"
      @keydown.up.prevent="focusPrev()"
      @keydown.escape="onEscape"
    >
      <span v-if="selectedLabel" class="truncate flex-1">{{ selectedLabel }}</span>
      <span v-else class="truncate flex-1 text-gray-400">{{ placeholder }}</span>
      <button
        v-if="clearable && modelValue != null"
        type="button"
        class="ml-1 text-gray-400 hover:text-gray-600 shrink-0"
        @click.stop="$emit('update:modelValue', null)"
      >
        <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
      </button>
      <FontAwesomeIcon
        :icon="['fas', 'chevron-down']"
        class="ml-1.5 text-gray-400 text-[10px] shrink-0 transition-transform"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <!-- Dropdown -->
    <teleport to="body">
      <div
        v-if="open"
        ref="dropdown"
        class="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg w-[var(--trigger-width)] max-w-[320px]"
        :style="dropdownStyle"
      >
        <!-- Search input -->
        <div class="p-2 border-b border-gray-100">
          <div class="relative">
            <FontAwesomeIcon
              :icon="['fas', 'search']"
              class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"
            />
            <input
              ref="searchInput"
              v-model="query"
              class="w-full text-sm border border-gray-200 rounded pl-8 pr-3 py-1.5 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
              placeholder="Search..."
              @keydown.down.prevent="focusNext"
              @keydown.up.prevent="focusPrev"
              @keydown.enter.prevent="selectFocused"
              @keydown.escape="onEscape"
            />
          </div>
        </div>

        <!-- Options list -->
        <div class="max-h-56 overflow-y-auto py-1" ref="optionsList">
          <button
            v-if="showNone"
            class="w-full text-left px-3 py-1.5 text-sm flex items-center hover:bg-gray-50"
            :class="{ 'bg-primary-50': focusedIndex === 0 }"
            @click="select(null)"
            @mouseenter="focusedIndex = 0"
          >
            <span
              class="w-1.5 h-1.5 rounded-full mr-2 shrink-0"
              :class="modelValue == null ? 'bg-primary-500' : 'bg-transparent'"
            />
            <span class="text-gray-500 italic">{{ nonePlaceholder }}</span>
          </button>
          <button
            v-for="(opt, i) in filtered"
            :key="opt.id"
            class="w-full text-left px-3 py-1.5 text-sm flex items-center hover:bg-gray-50 min-w-0"
            :class="{ 'bg-primary-50': focusedIndex === (showNone ? i + 1 : i) }"
            @click="select(opt.id)"
            @mouseenter="focusedIndex = showNone ? i + 1 : i"
          >
            <span
              class="w-1.5 h-1.5 rounded-full mr-2 shrink-0"
              :class="opt.id === modelValue ? 'bg-primary-500' : 'bg-transparent'"
            />
            <span v-if="opt.depth > 0" class="text-gray-300 shrink-0" v-html="'&nbsp;&nbsp;'.repeat(opt.depth)" />
            <span
              v-if="opt.typeName"
              class="text-[10px] font-medium px-1 py-0.5 rounded mr-1.5 shrink-0"
              :class="typeBadgeClass(opt.typeName)"
            >
              {{ opt.typeName }}
            </span>
            <span class="truncate">{{ opt.title }}</span>
          </button>
          <div
            v-if="!filtered.length"
            class="px-3 py-4 text-sm text-gray-400 text-center"
          >
            No results
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { TYPE_CLASSES } from './support_constants.js';

export default {
  name: 'Support_SearchableSelect',
  props: {
    modelValue: { type: [Number, String], default: null },
    options: { type: Array, required: true },
    placeholder: { type: String, default: 'Select...' },
    nonePlaceholder: { type: String, default: 'None (top-level)' },
    showNone: { type: Boolean, default: true },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    excludeId: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
      query: '',
      focusedIndex: -1,
      dropdownStyle: {},
    };
  },
  computed: {
    hierarchical() {
      const byId = {};
      for (const item of this.options) {
        byId[item.id] = { ...item, _children: [] };
      }
      const roots = [];
      for (const item of this.options) {
        if (item.parent && byId[item.parent]) {
          byId[item.parent]._children.push(byId[item.id]);
        } else {
          roots.push(byId[item.id]);
        }
      }
      const result = [];
      const walk = (nodes, depth) => {
        for (const n of nodes) {
          if (n.id === this.excludeId) continue;
          result.push({
            id: n.id,
            title: n.title,
            typeName: n.requirement_type_name || null,
            depth,
          });
          if (n._children.length) walk(n._children, depth + 1);
        }
      };
      walk(roots, 0);
      return result;
    },
    filtered() {
      if (!this.query.trim()) return this.hierarchical;
      const q = this.query.toLowerCase();
      return this.hierarchical.filter(
        (opt) => opt.title.toLowerCase().includes(q),
      );
    },
    totalOptions() {
      return (this.showNone ? 1 : 0) + this.filtered.length;
    },
    selectedLabel() {
      if (this.modelValue == null) return null;
      const opt = this.options.find((o) => o.id === this.modelValue);
      return opt ? opt.title : null;
    },
  },
  mounted() {
    document.addEventListener('click', this._onOutsideClick);
    window.addEventListener('scroll', this._reposition, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onOutsideClick);
    window.removeEventListener('scroll', this._reposition, true);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.open ? this.close() : this.openDropdown();
    },
    openDropdown() {
      this.open = true;
      this.query = '';
      this.focusedIndex = -1;
      this.$nextTick(() => {
        this._reposition();
        this.$refs.searchInput?.focus();
      });
    },
    close() {
      this.open = false;
      this.query = '';
    },
    // Escape closes the dropdown and stops there. Without stopPropagation the
    // event reaches the document-level handler in an enclosing SlideOverTray /
    // modal, so dismissing this dropdown would also close the drawer around it.
    // When the dropdown isn't open, let it bubble — then Escape means "close
    // the drawer", which is what the user expects.
    onEscape(e) {
      if (!this.open) return;
      e.stopPropagation();
      this.close();
    },
    select(id) {
      this.$emit('update:modelValue', id);
      this.close();
    },
    selectFocused() {
      if (this.focusedIndex < 0) return;
      if (this.showNone && this.focusedIndex === 0) {
        this.select(null);
      } else {
        const idx = this.showNone ? this.focusedIndex - 1 : this.focusedIndex;
        if (this.filtered[idx]) this.select(this.filtered[idx].id);
      }
    },
    focusNext() {
      if (this.focusedIndex < this.totalOptions - 1) this.focusedIndex++;
      this._scrollToFocused();
    },
    focusPrev() {
      if (this.focusedIndex > 0) this.focusedIndex--;
      this._scrollToFocused();
    },
    _scrollToFocused() {
      this.$nextTick(() => {
        const list = this.$refs.optionsList;
        if (!list) return;
        const el = list.children[this.focusedIndex];
        if (el) el.scrollIntoView({ block: 'nearest' });
      });
    },
    _reposition() {
      if (!this.open || !this.$refs.trigger) return;
      const rect = this.$refs.trigger.getBoundingClientRect();
      const dropdownHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;

      this.dropdownStyle = {
        '--trigger-width': rect.width + 'px',
        top: openUpward ? 'auto' : rect.bottom + 4 + 'px',
        bottom: openUpward
          ? window.innerHeight - rect.top + 4 + 'px'
          : 'auto',
        left: rect.left + 'px',
      };
    },
    _onOutsideClick(e) {
      if (
        this.open &&
        this.$refs.container &&
        !this.$refs.container.contains(e.target) &&
        this.$refs.dropdown &&
        !this.$refs.dropdown.contains(e.target)
      ) {
        this.close();
      }
    },
    typeBadgeClass(name) {
      return TYPE_CLASSES[name] || 'bg-gray-100 text-gray-600';
    },
  },
};
</script>
